import type { DropDownOptionBase } from "../../staff/DropDownMultiple";
import { convertDateRangeToEpoch } from "../../../utils/functions";
import type {
  IFilterData,
  IQuickFilter,
  QuickFilterQueryClause,
} from "./FilteredTable.types";

export type FilterComparisonType = NonNullable<IFilterData["comparison"]>;

export function getFilterApiFieldName(filter: IFilterData): string {
  return filter.fieldName ?? filter.name.toLocaleLowerCase();
}

export function serializePanelFilterToQueryValue(
  value: unknown,
  comparison: FilterComparisonType = "BY_ID",
  getPresetRange?: (optionId: string) => string,
): QuickFilterQueryClause | null {
  const cmp = comparison ?? "BY_ID";
  const asOptions = Array.isArray(value) ? (value as DropDownOptionBase[]) : [];

  const comparisonMap: Record<FilterComparisonType, () => QuickFilterQueryClause> = {
    CONTAINS: () => ({ value: String(value ?? ""), comparisonType: "CONTAINS" }),
    BETWEEN: () => ({ value: String(value ?? ""), comparisonType: "BETWEEN" }),
    BETWEEN_TS: () => ({
      value: convertDateRangeToEpoch(String(value ?? "")),
      comparisonType: "BETWEEN_TS",
    }),
    BETWEEN_TS_PRESET: () => {
      const firstId = asOptions[0]?.id ?? asOptions[0]?.name;
      const range = firstId && getPresetRange ? getPresetRange(String(firstId)) : "";
      return { value: range, comparisonType: "BETWEEN_TS" };
    },
    BY_NAME: () => ({
      value: asOptions.map((v) => v.name),
      comparisonType: "EQUAL",
    }),
    BY_NAME_CONTAINS: () => ({
      value: asOptions.map((v) => v.name),
      comparisonType: "CONTAINS_ANY",
    }),
    ARRAY_SEARCH_ID: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "ARRAY_CONTAINS",
      arraySearchField: "id",
    }),
    ARRAY_CONTAINS: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "ARRAY_CONTAINS",
    }),
    BY_ID: () => ({
      value: asOptions.map((v) => v.id),
    }),
    EQUAL: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "EQUAL",
    }),
    BOOLEAN: () => ({
      value: asOptions.map((v) => v.name === "Yes"),
      comparisonType: "EQUAL",
    }),
    IN: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "IN",
    }),
    NOT_EQUAL_OR_NULL: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "NOT_EQUAL_OR_NULL",
    }),
    NOT_IN: () => ({
      value: asOptions.map((v) => v.id),
      comparisonType: "NOT_IN",
    }),
    SPONSOR_NAME_TOKENS: () => ({
      value: asOptions.map((v) => String(v.id)),
      comparisonType: "SPONSOR_NAME_TOKENS",
    }),
    IS_DEFINED: () => ({
      value: [],
      comparisonType: "IS_DEFINED",
    }),
  };

  if (cmp === "IS_DEFINED") {
    if (asOptions.length === 0) {
      return null;
    }
    return comparisonMap.IS_DEFINED();
  }

  const queryValue = comparisonMap[cmp] ? comparisonMap[cmp]() : comparisonMap.BY_ID();
  const built = queryValue.value;
  if (
    (Array.isArray(built) && built.length === 0) ||
    (typeof built === "string" && built.trim() === "")
  ) {
    return null;
  }
  return queryValue;
}

export function getChipOwnedPanelNames(chip: IQuickFilter | null | undefined): string[] {
  return Object.keys(chip?.panelValues ?? {});
}

export function getChipOwnedQueryKeys(
  chip: IQuickFilter | null | undefined,
  filtersData: IFilterData[],
): string[] {
  if (!chip) {
    return [];
  }
  const keys = new Set<string>(Object.keys(chip.hiddenQuery ?? {}));
  for (const panelName of getChipOwnedPanelNames(chip)) {
    const filterConfig = filtersData.find((f) => f.name === panelName);
    if (filterConfig) {
      keys.add(getFilterApiFieldName(filterConfig));
    }
  }
  return [...keys];
}

export function buildChipQueryClauses(
  chip: IQuickFilter,
  filtersData: IFilterData[],
): Record<string, QuickFilterQueryClause> {
  const next: Record<string, QuickFilterQueryClause> = {
    ...(chip.hiddenQuery ?? {}),
  };

  for (const [panelName, options] of Object.entries(chip.panelValues ?? {})) {
    const filterConfig = filtersData.find((f) => f.name === panelName);
    if (!filterConfig) {
      continue;
    }
    const clause = serializePanelFilterToQueryValue(
      options,
      filterConfig.comparison,
    );
    if (!clause) {
      continue;
    }
    next[getFilterApiFieldName(filterConfig)] = clause;
  }

  return next;
}

export function buildQuickFilterDefs(
  quickFilters: IQuickFilter[],
  filtersData: IFilterData[],
): Record<string, Record<string, QuickFilterQueryClause>> {
  const defs: Record<string, Record<string, QuickFilterQueryClause>> = {};
  for (const chip of quickFilters) {
    defs[chip.key] = buildChipQueryClauses(chip, filtersData);
  }
  return defs;
}

export function stripChipFromFilterValues(
  filterValues: Record<string, unknown>,
  chip: IQuickFilter | null | undefined,
): Record<string, unknown> {
  if (!chip) {
    return filterValues;
  }
  const next = { ...filterValues };
  for (const panelName of getChipOwnedPanelNames(chip)) {
    next[panelName] = [];
  }
  return next;
}

export function stripChipFromQuery(
  query: Record<string, unknown>,
  chip: IQuickFilter | null | undefined,
  filtersData: IFilterData[],
): Record<string, unknown> {
  const next = { ...query };
  delete next.quickFilter;
  if (!chip) {
    return next;
  }
  for (const key of getChipOwnedQueryKeys(chip, filtersData)) {
    delete next[key];
  }
  return next;
}

export function queryClauseEquals(
  left: unknown,
  right: QuickFilterQueryClause,
): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

export function chipQueryClausesSatisfied(
  query: Record<string, unknown>,
  chip: IQuickFilter,
  filtersData: IFilterData[],
): boolean {
  if (query.quickFilter != null) {
    return false;
  }
  const expected = buildChipQueryClauses(chip, filtersData);
  return Object.entries(expected).every(([key, clause]) =>
    queryClauseEquals(query[key], clause),
  );
}

export function filterValuesMatchChip(
  filterValues: Record<string, unknown>,
  chip: IQuickFilter,
): boolean {
  if (String(filterValues.quickFilter ?? "") !== chip.key) {
    return false;
  }
  for (const [panelName, options] of Object.entries(chip.panelValues ?? {})) {
    if (JSON.stringify(filterValues[panelName]) !== JSON.stringify(options)) {
      return false;
    }
  }
  return true;
}

export function isQuickFilterSelectionResolved(
  chip: IQuickFilter,
  filterValues: Record<string, unknown>,
  query: Record<string, unknown>,
  filtersData: IFilterData[],
): boolean {
  if (!filterValuesMatchChip(filterValues, chip)) {
    return false;
  }
  return chipQueryClausesSatisfied(query, chip, filtersData);
}

export function applyChipToFilterValues(
  filterValues: Record<string, unknown>,
  chip: IQuickFilter,
): Record<string, unknown> {
  if (filterValuesMatchChip(filterValues, chip)) {
    return filterValues;
  }
  return {
    ...filterValues,
    quickFilter: chip.key,
    ...(chip.panelValues ?? {}),
  };
}

export function applyChipToQuery(
  query: Record<string, unknown>,
  chip: IQuickFilter,
  filtersData: IFilterData[],
): Record<string, unknown> {
  if (chipQueryClausesSatisfied(query, chip, filtersData)) {
    return query;
  }
  const next = { ...query };
  delete next.quickFilter;
  return {
    ...next,
    ...buildChipQueryClauses(chip, filtersData),
  };
}

export function ensureChipHiddenQuery(
  query: Record<string, unknown>,
  chip: IQuickFilter | null | undefined,
): Record<string, unknown> {
  let changed = false;
  const next: Record<string, unknown> = { ...query };
  if (next.quickFilter !== undefined) {
    delete next.quickFilter;
    changed = true;
  }
  if (!chip?.hiddenQuery) {
    return changed ? next : query;
  }
  for (const [key, clause] of Object.entries(chip.hiddenQuery)) {
    if (next[key] == null) {
      next[key] = clause;
      changed = true;
    }
  }
  return changed ? next : query;
}

export function findQuickFilter(
  quickFilters: IQuickFilter[],
  key: string | null | undefined,
): IQuickFilter | undefined {
  if (key == null || key === "") {
    return undefined;
  }
  return quickFilters.find((chip) => chip.key === String(key));
}
